import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import axiosInstance from '../../api/axiosConfig';

const UserProfileSection = () => {
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [editing, setEditing] = useState(false);
    const [userRole] = useState(localStorage.getItem('role') || '');
    const [roles, setRoles] = useState([]); // State for roles
    const canEdit = useMemo(() => {
        const normalized = String(userRole || '').toUpperCase();
        return ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'ADMIN'].includes(normalized);
    }, [userRole]);

    useEffect(() => {
        let mounted = true;

        const fetchData = async () => {
            const [employeesResult, rolesResult] = await Promise.allSettled([
                axiosInstance.get('/api/settings/employees'),
                axiosInstance.get('/api/settings/roles'),
            ]);

            if (!mounted) return;

            if (employeesResult.status === 'fulfilled') {
                const rows = Array.isArray(employeesResult.value?.data) ? employeesResult.value.data : [];
                const fetchedEmployees = rows.map((employee) => ({
                    ...employee,
                    role: Array.isArray(employee.role) ? employee.role : [employee.role].filter(Boolean),
                }));
                setEmployees(fetchedEmployees);
            } else {
                console.error('Failed to fetch settings employees:', employeesResult.reason);
                setEmployees([]);
            }

            if (rolesResult.status === 'fulfilled') {
                const normalizedRoles = Array.isArray(rolesResult.value?.data)
                    ? rolesResult.value.data
                        .map((role) => {
                            if (typeof role === 'string') return role;
                            if (role && typeof role === 'object') return role.roleName || role.name || role.code || '';
                            return '';
                        })
                        .filter(Boolean)
                    : [];
                setRoles(normalizedRoles);
            } else {
                console.error('Failed to fetch settings roles:', rolesResult.reason);
                setRoles([]);
            }
        };

        fetchData();

        return () => {
            mounted = false;
        };
    }, []);

    const handleEditClick = (employee) => {
        setSelectedEmployee(employee);
        setEditing(true);
    };

    const handleUpdate = () => {
        axiosInstance.post(`/api/settings/employees/update/${selectedEmployee.employeeId}`, selectedEmployee)
            .then(response => {
                const updatedEmployee = {
                    ...response.data,
                    role: Array.isArray(response.data?.role) ? response.data.role : [response.data?.role].filter(Boolean),
                };
                setEmployees(employees.map(emp => emp.employeeId === selectedEmployee.employeeId ? updatedEmployee : emp));
                setEditing(false);
                setSelectedEmployee(null);
            })
            .catch((error) => {
                console.error('Failed to update employee role settings:', error);
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedEmployee({
            ...selectedEmployee,
            [name]: value
        });
    };

    const handleRoleChange = (e) => {
        const { value } = e.target;
        setSelectedEmployee({
            ...selectedEmployee,
            role: typeof value === 'string' ? value.split(',') : value,
        });
    };

    return (
        <Box className="user-profile-section">
            <br></br>
            <h2>Roles Assigned to Employees</h2>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>First Name</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.map(employee => (
                            <TableRow key={employee.employeeId}>
                                <TableCell>{employee.firstName}</TableCell>
                                <TableCell>{employee.lastName}</TableCell>
                                <TableCell>{employee.email}</TableCell>
                                <TableCell>{employee.phone}</TableCell>
                                <TableCell>{Array.isArray(employee.role) ? employee.role.join(', ') : ''}</TableCell>
                                <TableCell>
                                    {canEdit && (
                                        <Button onClick={() => handleEditClick(employee)}>Edit</Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {editing && selectedEmployee && (
                <Box className="edit-form" sx={{ mt: 3 }}>
                    <h3>Edit Employee</h3>
                    <TextField
                        label="First Name"
                        type="text"
                        name="firstName"
                        value={selectedEmployee.firstName}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Last Name"
                        type="text"
                        name="lastName"
                        value={selectedEmployee.lastName}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Email"
                        type="email"
                        name="email"
                        value={selectedEmployee.email}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Phone"
                        type="text"
                        name="phone"
                        value={selectedEmployee.phone}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            name="role"
                            value={selectedEmployee.role}
                            onChange={handleRoleChange}
                            multiple
                        >
                            {roles.map(role => (
                                <MenuItem key={role} value={role}>{role}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button onClick={handleUpdate} variant="contained" sx={{ mr: 2 }}>Save</Button>
                    <Button onClick={() => setEditing(false)} variant="outlined">Cancel</Button>
                </Box>
            )}
        </Box>
    );
};

export default UserProfileSection;
